/*
  global documentFake, SportsFeatures,
  NetworkConfig, BaseUrl, FontName, SingleSignOnEnvironment, Version
*/
import React from 'react';
import _ from 'lodash';
import {
  addDays,
  addSeconds,
  differenceInDays,
  differenceInSeconds,
  FORMAT_DAY_OF_WEEK,
  FORMAT_MONTH_DAY_YEAR,
  FORMAT_MONTH_SLASH_DAY,
  FORMAT_TIME,
  format,
  getTimestamp,
  isAfter,
  isBefore,
  isBetween,
  isSameDay,
  msToDate,
  now,
  stringToDate,
} from 'helpers/DateTimeHelper';
import Error404 from 'screens/Error404';
import Error500 from 'screens/Error500';
import { formatDurationInSeconds, formatMetadata } from 'helpers/VideoHelper';
import { setCookie, getCookie, cookieFactory } from 'helpers/Storage';
import Regex from 'helpers/Regex';
import { manageAccountCookies, setMVPDCookie, getMVPDCookie } from 'index/AuthHelper';
import { stripAuthProperties, parseJwt } from 'helpers/AuthHelper';
import { getSitePlatform } from 'helpers/BreakpointHelper';
import { NETWORK_LOGO_IMAGE, getDynamicImage } from 'helpers/ImageHelper';
import { getBaseContentUrl, helperGetApiKey, apiErrorHandler } from 'helpers/ApiHelper';
import { refreshUserAuth, setSDKAccessToken } from 'index/AppActions';
import { getLegacyDomain } from './Account';

export const FNC = 'fnc';
export const FOX = 'fox';
export const NATION = 'foxnation'; //eslint-disable-line

export const LABEL_CLIPS_AND_EXTRAS = 'Clips & Extras';

export const TYPE_EPG_LISTING = 'EPGListing';
const TYPE_FULL_EPISODE = 'fullEpisode';
const TYPE_SPORTING_EVENT = 'sportingEvent';

export const SERIES_TYPE_SERIES_COLLECTION = 'seriesCollection';
export const CARD_TYPE_POSTER = 'poster';
export const CARD_TYPE_EPISODE = 'episode';
export const PAGE_TYPE_ARTICLE = 'article';
export const PAGE_TYPE_CREDITS = 'credits';
export const PAGE_TYPE_SEE_ALL = 'see-all';
export const PAGE_TYPE_WATCHLIST = 'watchlist';

const PREVIEW_PASS_5_MVPD = 'TempPass_fbcfox_5min';
const PREVIEW_PASS_60_MVPD = 'TempPass_fbcfox_60min';

export const COLLECTION_TYPE_GENRE = 'genre';

export const NEWS_GENRE = 'News';
export const CONTINUE_WATCHING_BADGE = 'CONTINUE WATCHING';

export const TAB_PRIMETIME = _.get(window.Language, 'global_primetimeTab');
export const TAB_SCHEDULE = _.get(window.Language, 'global_scheduleTab');

const NO_AUTHORIZING_NETWORK = 'no authorizing network';

export const NAME_RECOMMENDED_SERIES = 'You May Also Like';
export const PANEL_RECOMMENDED_SERIES = 'more to watch';

export const LINK_REL = 'noopener noreferrer';

export const PANEL_TYPE_COUNTDOWN = 'countdown';
export const PANEL_TYPE_PLAYER_BANNER = 'playerBanner';
export const PANEL_TYPE_PROMOS = 'promos';
export const PANEL_TYPE_SHOWS = 'shows';
export const PANEL_TYPE_VIDEOS = 'videos';

export const VIDEO_TYPE_TRAILER = 'trailer';
export const VIDEO_TYPE_WATCH = 'watch';
export const VIDEO_TYPE_LIVE = 'live';

export const DISPLAY_TYPE_DESTINATION = 'destination';
const NETWORKS_DISPLAY_ORDER_NAME = 'network-display-order';
const MVPD_SUBSCRIPTION_COOKIE_NAME = 'mvpd-subscription';
const EMAIL_CAPTURED_COOKIE_NAME = 'email-captured';
export const NATION_ENTITLED_COOKIE_NAME = 'nation-entitled';
export const NATION_WATCH_FREE = 'nation-watch-free';
export const CCPA_OPT_OUT_COOKIE_NAME = 'usprivacy';
export const CCPA_DO_NOT_SELL_NOT_SEEN = '1---';
export const CCPA_DO_NOT_SELL_SEEN = '1YNN';
export const CCPA_DO_NOT_SELL_CHECKED = '1YYN';

const SEARCH_VALUE_COOKIE = 'SEARCH_VALUE_COOKIE';

export const HTML_ENTITY_BULLET = String.fromCharCode(8226);

export const getNetworkBranding = () => _.get(NetworkConfig, 'branding');

export const getNetworkBrandingName = () => _.get(NetworkConfig, 'name');

export const getNetworksDisplayOrder = () => {
  let networkDisplayOrder = getNetworkDisplayOrder();
  if (!networkDisplayOrder) {
    networkDisplayOrder = _.get(window.ExternalConfig, 'auth.success.networksDisplayOrder', []);
    if (networkDisplayOrder.length > 0) {
      setCookie({ name: NETWORKS_DISPLAY_ORDER_NAME, value: networkDisplayOrder });
      setNetworkDisplayOrder(networkDisplayOrder);
    }
  }
  return networkDisplayOrder;
};

export const getNetworkList = () =>
  _.get(window.ExternalConfig, 'auth.success.networksDisplayOrder', []);

export const canDisplayUpsell = () => {
  const entitlementCookieName = _.get(window.ExternalConfig, 'entitlementCookieName');
  return !getCookie({ name: entitlementCookieName });
};

export const isNationEntitled = () => {
  const entitlementCookieName = _.get(window.ExternalConfig, 'entitlementCookieName');
  return getCookie({ name: entitlementCookieName });
};

export const getMVPDFromCookie = () => {
  const mvpdCookie = getMVPDCookie();
  const mvpd = _.get(mvpdCookie, 'mvpd', null);
  return mvpd;
};

/* eslint-disable */
export const getPurchaseSourceCookie = () => {
  const purchaseSource = getCookie({ name: 'purchaseSource' });
  return purchaseSource || '';
};
/* eslint-enable */

const getVideoStatus = item => {
  const originalDate = item?.startDate;
  const startsAt = stringToDate(originalDate);
  const endsAt = addSeconds(stringToDate(item?.startDate), item?.durationInSeconds);

  if (isLive && startsAt && endsAt) {
    const videoIsUpcoming = isBefore(now(), startsAt);
    const videoIsLive = isBetween(now(), startsAt, endsAt);
    if (videoIsUpcoming) return 'upcoming';
    if (videoIsLive) return 'live';
  }
  return null;
};

export const hasEntitlementOverride = () => {
  const entitlementCookieName = _.get(window.ExternalConfig, 'entitlementCookieName');
  const entitlementCookie = getCookie({ name: entitlementCookieName });
  if (entitlementCookie) {
    const isNation = _.get(window.ExternalConfig, 'analytics.app_name', false) === NATION;
    if (isNation) return 'foxnation';
  }
  return false;
};

export const parseJSON = data => {
  try {
    return JSON.parse(data);
  } catch (e) {
    return null;
  }
};

export const getFontClassName = style => _.get(style, `${FontName}Font`);

export const isClip = videoType => videoType === 'clip';
// TODO: Remove "Teaser Trailer" check
const isClipTrailer = clipType => clipType === 'Trailer' || clipType === 'Teaser Trailer';
export const isEpisode = videoType => videoType === TYPE_FULL_EPISODE;
export const isLive = type => type === TYPE_EPG_LISTING;
export const isMovie = videoType => videoType === 'movie';
const isNews = seriesType => seriesType === 'news';
export const isNew = item => item?.contentFlags?.[0]?.toLowerCase() === 'new';
export const isSeries = seriesType => seriesType === 'series';
export const isSeriesCollection = seriesType => seriesType === SERIES_TYPE_SERIES_COLLECTION;
export const isSpecial = seriesType => seriesType === 'special' || seriesType === 'other';
export const isCategoryTypePersonality = categoryType => categoryType === 'personality';
export const isTypeCategory = type => type === 'Category';
export const isChannelType = categoryType => categoryType === 'destination';
export const isTypePromo = type => type === 'Promo';
export const isTypeScreenPanel = type => type === 'ScreenPanel';
export const isTypeSeries = type => type === 'Series';
export const isTypeSeason = type => type === 'Season';
export const isTypeEPGListing = type => type === 'EPGListing';
export const isTypeVideo = type => type?.toLowerCase() === 'video';
export const isSports = seriesType => seriesType === 'sportingEvent';
export const isAudio = item => item?.audioOnly || item?.contentFlags?.includes('AUDIO ONLY');
export const isEpgLive = item => getVideoStatus(item) === 'live';
export const isUpcoming = item => getVideoStatus(item) === 'upcoming';
export const isContinueWatching = item =>
  _.toLower(item?.contentLabel?.[0]) === 'continue watching';

export const isShowcase = panel =>
  panel?.collectionType === 'showcase' || panel?.collectionType === 'showcaseCarouselContent';

export const isContinueListening = item =>
  item?.audioOnly
    ? _.includes(item?.contentFlags, 'Continue Watching') ||
      _.includes(item?.contentFlags, 'Continue Listening')
    : false;
export const isShowcaseCountdown = video =>
  video?.eventData?.hasCountdown && isAfter(stringToDate(video?.eventData?.startTime), now());

export const isNationLicensed = video => {
  if (video) {
    const contentSKUResolved = _.get(video, 'contentSKUResolved');
    return !_.find(contentSKUResolved, ({ baseId }) => baseId === 'us.foxnationfree');
  }
  return false;
};
export const DEVICE_ANDROID = 'DEVICE_ANDROID';
export const DEVICE_IOS = 'DEVICE_IOS';

export const sportsFeatures = () => SportsFeatures;
const SPORTS_MAX_DESC_LENGTH = 150;
export const HERO_MAX_DESC_LENGTH = 112;
export const HERO_MAX_KICKER_LENGTH = 50;

export const API_BASEURL_OVERRIDE = 'API_BASEURL_OVERRIDE';
export const DEBUG_PANEL_AD_DEBUG = 'DEBUG_PANEL_AD_DEBUG';
export const DEBUG_PANEL_AD_SITE_SECTION = 'DEBUG_PANEL_AD_SITE_SECTION';
export const DEBUG_PANEL_API_BASEURL = 'DEBUG_PANEL_API_BASEURL';
export const DEBUG_PANEL_BLACKOUT_FETCH_INTERVAL = 'DEBUG_PANEL_BLACKOUT_FETCH_INTERVAL';
export const DEBUG_PANEL_CITY = 'DEBUG_PANEL_CITY';
export const DEBUG_PANEL_CITY_LAT = 'DEBUG_PANEL_CITY_LAT';
export const DEBUG_PANEL_CITY_LONG = 'DEBUG_PANEL_CITY_LONG';
export const DEBUG_PANEL_CLIENTLESS_RECURRING_PRE_AUTHZ_REFRESH =
  'DEBUG_PANEL_CLIENTLESS_RECURRING_PRE_AUTHZ_REFRESH';
export const DEBUG_PANEL_CLIENTLESS_TOKEN_EXPIRE_REFRESH =
  'DEBUG_PANEL_CLIENTLESS_TOKEN_EXPIRE_REFRESH';
export const DEBUG_PANEL_EMAIL_CAPTURE = 'DEBUG_PANEL_EMAIL_CAPTURE';
export const DEBUG_PANEL_FLAG_ADDITIONAL_POSTER_CURRENT_HOVER =
  'DEBUG_PANEL_FLAG_ADDITIONAL_POSTER_CURRENT_HOVER';
export const DEBUG_PANEL_FLAG_ADDITIONAL_POSTER_SIMPLIFIED_HOVER =
  'DEBUG_PANEL_FLAG_ADDITIONAL_POSTER_SIMPLIFIED_HOVER';
export const DEBUG_PANEL_FLAG_BLACKOUT = 'DEBUG_PANEL_FLAG_BLACKOUT';
export const DEBUG_PANEL_FLAG_BUYTHROUGH_EXTERNAL_SIGNUP_LOGIN =
  'DEBUG_PANEL_FLAG_BUYTHROUGH_EXTERNAL_SIGNUP_LOGIN';
export const DEBUG_PANEL_FLAG_CAROUSEL_V2 = 'DEBUG_PANEL_FLAG_CAROUSEL_V2';
export const DEBUG_PANEL_FLAG_CCPA_OPT_OUT = 'DEBUG_PANEL_FLAG_CCPA_OPT_OUT';
export const DEBUG_PANEL_FLAG_COUNTDOWN_BANNER = 'DEBUG_PANEL_FLAG_COUNTDOWN_BANNER';
export const DEBUG_PANEL_FLAG_DISABLE_REDIRECT = 'DEBUG_PANEL_FLAG_DISABLE_REDIRECT';
export const DEBUG_PANEL_FLAG_EPG_SEARCH = 'DEBUG_PANEL_FLAG_EPG_SEARCH';
export const DEBUG_PANEL_FLAG_EPGV2 = 'DEBUG_PANEL_FLAG_EPGV2';
export const DEBUG_PANEL_FLAG_FNCV2 = 'DEBUG_PANEL_FLAG_FNCV2';
export const DEBUG_PANEL_FLAG_GLOBAL_PLAYER = 'DEBUG_PANEL_FLAG_GLOBAL_PLAYER';
export const DEBUG_PANEL_FLAG_GLOBAL_PLAYER_AUTOPLAY = 'DEBUG_PANEL_FLAG_GLOBAL_PLAYER_AUTOPLAY';
export const DEBUG_PANEL_FLAG_HERO_CAROUSEL = 'DEBUG_PANEL_FLAG_HERO_CAROUSEL';
export const DEBUG_PANEL_FLAG_UPNEXT_LIVE_EVENTS = 'DEBUG_PANEL_FLAG_UPNEXT_LIVE_EVENTS';
export const DEBUG_PANEL_FLAG_IMMERSIVE_SHOWCASE = 'DEBUG_PANEL_FLAG_IMMERSIVE_SHOWCASE';
export const DEBUG_PANEL_FLAG_MOBILE_WEB_ROADBLOCK = 'DEBUG_PANEL_FLAG_MOBILE_WEB_ROADBLOCK';
export const DEBUG_PANEL_FLAG_MVPD_DEEPLINK = 'DEBUG_PANEL_FLAG_MVPD_DEEPLINK';
export const DEBUG_PANEL_FLAG_MVPD_SSO = 'DEBUG_PANEL_FLAG_MVPD_SSO';
export const DEBUG_PANEL_FLAG_NATION_DIGITAL_CHANNEL = 'DEBUG_PANEL_FLAG_NATION_DIGITAL_CHANNEL';
export const DEBUG_PANEL_FLAG_NATION_UNGATED_CTA = 'DEBUG_PANEL_FLAG_NATION_UNGATED_CTA';
export const DEBUG_PANEL_FLAG_PERSONALITY_REDESIGN = 'DEBUG_PANEL_FLAG_PERSONALITY_REDESIGN';
export const DEBUG_PANEL_FLAG_PREVIEW_MODE = 'DEBUG_PANEL_FLAG_PREVIEW_MODE';
export const DEBUG_PANEL_FLAG_PREVIEW_PASS = 'DEBUG_PANEL_FLAG_PREVIEW_PASS';
export const DEBUG_PANEL_FLAG_PREVIEW_PASS_60_MIN = 'DEBUG_PANEL_FLAG_PREVIEW_PASS_60_MIN';
export const DEBUG_PANEL_FLAG_SECOND_SCREEN_PROFILE = 'DEBUG_PANEL_FLAG_SECOND_SCREEN_PROFILE';
export const DEBUG_PANEL_FLAG_SERIES_ANONYMOUS = 'DEBUG_PANEL_FLAG_SERIES_ANONYMOUS';
export const DEBUG_PANEL_FLAG_SERIES_V2 = 'DEBUG_PANEL_FLAG_SERIES_V2';
export const DEBUG_PANEL_FLAG_STRIKE_BANNER = 'DEBUG_PANEL_FLAG_STRIKE_BANNER';
export const DEBUG_PANEL_FLAG_TILE_LIVE_TILE_V2 = 'DEBUG_PANEL_FLAG_TILE_LIVE_TILE_V2';
export const DEBUG_PANEL_FLAG_USE_MARINE_API_KEY = 'DEBUG_PANEL_FLAG_USE_MARINE_API_KEY';
export const DEBUG_PANEL_FLAG_WATCH_V2 = 'DEBUG_PANEL_FLAG_WATCH_V2';
export const DEBUG_PANEL_LOCATION_COOKIE_DURATION = 'DEBUG_PANEL_LOCATION_COOKIE_DURATION';
export const DEBUG_PANEL_TIMEOUT_DISABLED = 'DEBUG_PANEL_TIMEOUT_DISABLED';
export const DEBUG_PANEL_USER = 'DEBUG_PANEL_USER';
export const DEBUG_PANEL_VIDEO_CDN = 'DEBUG_PANEL_VIDEO_CDN';
export const DEBUG_PANEL_VIDEO_ORIGIN = 'DEBUG_PANEL_VIDEO_ORIGIN';

const { getValue: getAdDebug } = cookieFactory(DEBUG_PANEL_AD_DEBUG);
const { getValue: getAdSiteSection } = cookieFactory(DEBUG_PANEL_AD_SITE_SECTION);
const { getValue: getPrivacyParameterCookie, setValue: setPrivacyParameterCookie } = cookieFactory(
  CCPA_OPT_OUT_COOKIE_NAME,
);
const { getValue: getLatitude, setValue: setLatitude } = cookieFactory('COOKIE_GEO_LOCATION_LAT');
const { getValue: getLongitude, setValue: setLongitude } = cookieFactory(
  'COOKIE_GEO_LOCATION_LONG',
);
const { getValue: getCookieTimestamp, setValue: setCookieTimeStamp } = cookieFactory(
  'COOKIE_GEO_LOCATION_DATE_MS',
);
const { setValue: setCookieModalTimeStamp } = cookieFactory('COOKIE_GEO_LOCATION_MODAL_MS');
const { getValue: getNetworkDisplayOrder, setValue: setNetworkDisplayOrder } = cookieFactory(
  NETWORKS_DISPLAY_ORDER_NAME,
);
const { getValue: isMvpdSubscription } = cookieFactory(MVPD_SUBSCRIPTION_COOKIE_NAME);

const {
  getValue: getSearchValueCookie,
  setValue: setSearchValueCookie,
  removeValue: removeSearchValueCookie,
} = cookieFactory(SEARCH_VALUE_COOKIE);

const { getValue: getEmailCaptureCookie, setValue: setEmailCaptureCookie } = cookieFactory(
  EMAIL_CAPTURED_COOKIE_NAME,
);

export function onRefreshSuccess({ store }) {
  const saveData = data => store.dispatch(refreshUserAuth(data));
  return function(account) {
    const sanitizedAccount = stripAuthProperties(account);
    manageAccountCookies({ account: sanitizedAccount });
    const isMVPD = account?.mvpd;
    if (isMVPD) {
      const currMVPD = getMVPDCookie();
      setMVPDCookie({ ...currMVPD, ...sanitizedAccount });
    }
    saveData(sanitizedAccount);
    store.dispatch(setSDKAccessToken({ accessToken: account?.accessToken }));
  };
}

export async function createSDKs(onRefreshSuccess = () => {}) {
  let profile = {};
  let profileFavorites = {};
  let profileBookmarks = {};
  let auth = {};
  if (!isStaticRender()) {
    const module = await import('@foxcorp/foxkit-profile-js');
    const authSDK = await import('@foxcorp/foxkit-auth-js');
    const baseConfigUrl = getBaseContentUrl();
    const baseConfigLength = baseConfigUrl.length;
    const baseUrl =
      baseConfigUrl.substring(baseConfigLength - 1, baseConfigLength) === '/'
        ? baseConfigUrl.substring(0, baseConfigLength - 1)
        : baseConfigUrl;
    const config = sdk => ({
      appName: 'FOX NATION',
      appVersion: Version,
      apiKey: helperGetApiKey(),
      environment: SingleSignOnEnvironment,
      baseUrl,
      statefulRefreshToken: {
        authCookieName: 'FOXSSOACCT',
        authCookieDomain: getLegacyDomain(),
        checkRefreshOnInit: sdk === 'profile',
        use: true,
        onRefreshSuccess,
        onRefreshError: error => apiErrorHandler({ error }),
      },
    });

    const authConfig = new authSDK.AuthConfig(config('auth'));
    auth = new authSDK.Auth(authConfig);
    const profileConfig = new module.ProfileConfig(config('profile'));
    profile = new module.Profile(profileConfig);
    const favoriteConfig = new module.ProfileConfig(config('favorite'));
    const bookmarkConfig = new module.ProfileConfig(config('bookmarks'));
    profileFavorites = new module.ProfileFavoritesV4(favoriteConfig);
    profileBookmarks = new module.ProfileBookmarks(bookmarkConfig);
  }
  return { profile, profileFavorites, profileBookmarks, auth };
}

export const analyticsWatcher = () =>
  new Promise(resolve => {
    if (window?.analytics?.initialized) return resolve(true);
    // 50 count x 50ms interval = 2.5 sec
    let counter = 0;
    const isLoaded = setInterval(() => {
      const isAnalyticsInitialized = window?.analytics?.initialized;
      if (counter > 50 || isAnalyticsInitialized) {
        clearInterval(isLoaded);
        resolve(true);
      } else {
        counter += 1;
      }
    }, 50);
    return false;
  });

export {
  getAdDebug,
  getAdSiteSection,
  getCookieTimestamp,
  getEmailCaptureCookie,
  getLatitude,
  getLongitude,
  getPrivacyParameterCookie,
  getSearchValueCookie,
  isMvpdSubscription,
  removeSearchValueCookie,
  setCookieModalTimeStamp,
  setCookieTimeStamp,
  setEmailCaptureCookie,
  setLatitude,
  setLongitude,
  setPrivacyParameterCookie,
  setSearchValueCookie,
};

export const isDescTooLong = description => {
  if (!sportsFeatures() || !description) return false;
  if (description.length > SPORTS_MAX_DESC_LENGTH) return true;
  return false;
};

export const getContentTypeName = videoType => {
  if (isMovie(videoType)) return 'Movie';
  return 'Show';
};

export const getVideoTypeName = type => {
  if (isMovie(type)) return 'Movie';
  if (isClip(type)) return 'Clip';
  return 'Episode';
};

export const getDisplayBrand = item => {
  const displayBrand = _.get(item, 'displayBrand');
  if (displayBrand) return displayBrand;
  const network = _.get(item, 'network');

  return network;
};

export const getNetworkLogoUrl = (network, size, logo = window.ExternalConfig.networkLogo) => {
  if (!network || !network.length || !logo) return null;
  const breakpoint = getSitePlatform();
  let networkTemp = network;
  if (networkTemp === 'fbc-fox') networkTemp = FOX;
  const networkComma = network.indexOf(',');
  const networkId = networkComma > 0 ? network.substring(0, networkComma) : networkTemp;
  const url = logo.replace('{network}', networkId);
  return getDynamicImage({
    breakpoint,
    context: NETWORK_LOGO_IMAGE,
    url,
  });
};

export const activePlansHasFreeTrial = activePlans => {
  if (!activePlans) return null;
  const promoPackages = activePlans?.PkgsOrProdsResponseMsg?.pkgWithPromos;
  let isFreeTrialActive = false;
  promoPackages?.forEach(pkg => {
    pkg?.promoMsg?.forEach(promo => {
      if (promo?.isFreeTrail && (!promo?.amount || promo?.amount <= 0)) {
        isFreeTrialActive = true;
      }
    });
  });
  return isFreeTrialActive;
};

export const mobileDeviceType = () => {
  const userAgent = _.get(window, 'navigator.userAgent', null);
  if (!userAgent) return null;
  if (userAgent.match(/Android/i)) return DEVICE_ANDROID;
  if (userAgent.match(/iPhone|iPad|iPod/i)) return DEVICE_IOS;
  return null;
};

export const disableSmoothScrollMSIE = () => {
  const userAgent = _.get(window, 'navigator.userAgent', null);
  if (!userAgent) return null;
  if (userAgent.match(/TRIDENT/i)) {
    window.addEventListener('mousewheel', e => {
      e.preventDefault();
      window.scrollTo(0, window.pageYOffset - e.wheelDelta);
    });
  }
  return null;
};

export const getUrlParameter = name => {
  const urlName = name.replace(/\[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp(`[\\?&]${urlName}=([^&#]*)`);
  const results = regex.exec(window.location.search) || regex.exec(window.location.href);
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

export const isStaticRender = () => typeof documentFake === 'boolean';

export const getAuthZEntitlementIssues = response => {
  if (!response) return null;
  const responseStatus = response?.status;
  const errorStatus = response?.error?.errorCode;
  let authZEntitlementIssues;
  if (responseStatus) authZEntitlementIssues = _.get(response, 'data.entitlementIssues', []);
  else if (errorStatus) authZEntitlementIssues = _.get(response, 'error.entitlementIssues', []);

  const isAuthZ = authZEntitlementIssues?.length > 0;
  if (!isAuthZ) return null;

  const entitlementIssues = _.map(authZEntitlementIssues, i => ({
    ...i,
    statusCode: responseStatus || errorStatus,
  }));
  return entitlementIssues;
};

export const determineErrorFromResponse = action => {
  if (isStaticRender()) return null;

  const response = _.get(action, 'payload.response');
  const responseStatus = _.get(response, 'status');

  const authZEntitlementIssues = getAuthZEntitlementIssues(response);
  if (authZEntitlementIssues) return authZEntitlementIssues;

  const is400Error = responseStatus >= 400 && responseStatus <= 499;
  if (is400Error) return 404;

  const is500Error = responseStatus >= 500 && responseStatus <= 599;
  if (is500Error) return 500;

  return null;
};

export const determineErrorIfStaticRender = () => {
  if (isStaticRender()) return null;
  return 404;
};

const getError500Component = () => {
  const breakpoint = getSitePlatform();
  const branding = getNetworkBranding();

  const props = {
    className: 'ft-error',
    breakpoint,
    branding,
  };
  return <Error500 data-test="error-500" {...props} />;
};

const getError404Component = () => {
  const breakpoint = getSitePlatform();
  const branding = getNetworkBranding();

  const props = {
    className: 'ft-error',
    breakpoint,
    branding,
  };
  return <Error404 data-test="error-404" {...props} />;
};

export const getErrorComponent = errorCode => {
  if (errorCode === 500) return getError500Component();
  if (errorCode === 404) return getError404Component();
  return null;
};

export const forceHttps = url => _.replace(url, 'http://', '//');

export const urlIsExternal = url => {
  if (_.startsWith(url, 'http')) return true;
  if (_.startsWith(url, 'www')) return true;
  return false;
};

export const formatDirectUrl = url => {
  if (_.startsWith(url, 'www')) return `http://${url}`;
  return url;
};

export const getUrlTarget = url => {
  if (urlIsExternal(url)) {
    return {
      target: '_blank',
      rel: LINK_REL,
    };
  }
  return {};
};

export const truncateText = (text, charCount = 30, append = '...') => {
  if (!text) return null;
  if (text.length < charCount) return text;
  const truncatedText = text.substr(0, charCount + append.length);
  const truncatedTextWithoutBrokenWords = truncatedText
    .split(' ')
    .slice(0, -1)
    .join(' ');
  const result = `${truncatedTextWithoutBrokenWords}${append}`;
  return result;
};

export const truncateDesc = (description, enableFeature) => {
  if (isDescTooLong(description) && enableFeature) {
    return `${description.substring(0, SPORTS_MAX_DESC_LENGTH)}...`;
  }
  return description;
};

export const isArticleTab = tab => {
  const { url } = tab;
  return url && url.indexOf('article/') !== -1;
};

export const isSportsVideoLive = video => {
  const status = _.get(video, 'contentLabel[0]', '');
  return status.toLowerCase() === 'live';
};

export const isSportsContent = video => {
  const seriesType = _.get(video, 'seriesType');
  const isSportsType = seriesType === TYPE_SPORTING_EVENT;
  const hasSportsFeatures = sportsFeatures();
  return !!(isSportsType && hasSportsFeatures);
};

export const concatToUrl = (url, stringToConcat) => {
  if (!url || !stringToConcat) return url;
  // remove empty question mark at the end.
  let newUrl = url.replace(/\?$/, '');
  const hasQuery = newUrl.indexOf('?') === -1;
  const queryGlue = hasQuery ? '?' : '&';
  // replace && to &
  newUrl = `${newUrl}${queryGlue}${stringToConcat}`.replace(/&&/, '&');
  return newUrl;
};

export const checkContentLabel = ({ video, label, labels }) => {
  const contentLabel = _.get(video, 'contentLabel', null);
  if (!contentLabel) return false;
  const contentLabels = _.map(contentLabel, l => l.toLowerCase());
  let hasLabel = false;
  if (label && _.includes(contentLabels, label.toLowerCase())) hasLabel = true;
  if (labels) {
    _.forEach(labels, l => {
      if (_.includes(contentLabels, l.toLowerCase())) hasLabel = true;
    });
  }
  return hasLabel;
};

export const videoIsLive = video => {
  const livePlayerScreenUrl = _.get(video, 'livePlayerScreenUrl', null);
  return !!livePlayerScreenUrl;
};

export const isInternalLink = (link, base) => {
  const baseLink = base || BaseUrl;
  if (!link || !baseLink) return false;

  const http = 'http://';
  const https = 'https://';
  const startsWith = _.startsWith(link.replace(https, http), baseLink.replace(https, http));
  return !!startsWith;
};

export const getSeasonEpisodeCounts = ({ currentSeason, seasonCount, episodeCount, useComma }) => {
  if (!seasonCount && !episodeCount) return null;
  let seasonString = '';
  let episodeString = '';
  if (seasonCount) {
    const seasonLabel = seasonCount > 1 ? 'Seasons' : 'Season';
    if (seasonCount > 1 || !currentSeason) {
      seasonString = `${seasonCount} ${seasonLabel}`;
    } else {
      seasonString = `${seasonLabel} ${currentSeason.seasonNumber}`;
    }
  }
  if (episodeCount) {
    const episodeLabel = episodeCount > 1 ? 'Episodes' : 'Episode';
    episodeString = `${episodeCount} ${episodeLabel}`;
  }
  const hideSeparator = !seasonCount || !episodeCount;
  let separator = '';
  if (!hideSeparator) separator = useComma ? ', ' : ' ';
  return `${seasonString}${separator}${episodeString}`;
};

export const getShowType = ({ videoType = '', seriesType }) => {
  if (isSeries(seriesType) || isSpecial(seriesType) || isNews(seriesType)) return 'Show';
  if (isMovie(videoType) || isMovie(seriesType)) return 'Movie';
  return '';
};

export const getLiveMetaData = ({ startDate, durationInSeconds }) => {
  const startTime = startDate ? format(stringToDate(startDate), FORMAT_TIME) : '';
  const formattedDuration = formatDurationInSeconds(durationInSeconds);
  return formatMetadata([startTime, formattedDuration]);
};

export const getMovieMetadata = ({ releaseYear = '', durationInSeconds }) => {
  const formattedDuration = formatDurationInSeconds(durationInSeconds);
  return formatMetadata([releaseYear, formattedDuration]);
};

export const getOriginalAirdateMetaData = ({ originalAirDate, durationInSeconds }) => {
  const airDate = originalAirDate
    ? format(stringToDate(originalAirDate), FORMAT_MONTH_DAY_YEAR)
    : '';
  const formattedDuration = formatDurationInSeconds(durationInSeconds);
  return formatMetadata([airDate, formattedDuration]);
};

export const castProcessor = actor => {
  const name = _.get(actor, 'name', null);
  return name;
};

export const getVideoIdFromUrl = url => {
  if (!url) return null;
  const urlParts = url.split('/');
  const videoId = urlParts[2];
  return videoId;
};

export const isUngatedVideo = video => {
  const { availabilityTags } = video?.trackingData?.properties || [];
  return (
    availabilityTags &&
    availabilityTags.map(text => text.toLowerCase()).find(text => text === 'free')
  );
};

export const getRequiresAuth = ({ video, isLive = false }) => {
  let requiresAuthVod = _.get(video, 'requiresAuth', false);
  let requiresAuthLive = _.get(video, 'requiresAuthLive', false);

  const requiredEntitlements = _.get(video, 'requiredEntitlements', null);
  if (requiredEntitlements) {
    const vodRequirements = _.get(requiredEntitlements, 'vod', []);
    const liveRequirements = _.get(requiredEntitlements, 'live', []);
    requiresAuthVod = vodRequirements.length > 0 || requiresAuthVod;
    requiresAuthLive = liveRequirements.length > 0 || requiresAuthLive;
  }

  return isLive ? requiresAuthLive : requiresAuthVod;
};

export const getAuthForPanelVideo = (item, isLive, accessToken) => {
  let mvpdid = '';
  if (accessToken) mvpdid = parseJwt(accessToken)?.mvpdid;

  return (
    getRequiresAuth({ video: item, isLive }) ||
    mvpdid === PREVIEW_PASS_60_MVPD ||
    mvpdid === PREVIEW_PASS_5_MVPD
  );
};

export const getAuthorizingNetwork = (item, isLive, accessToken) => {
  const requiresAuth = getAuthForPanelVideo(item, isLive, accessToken);
  if (!requiresAuth) return NO_AUTHORIZING_NETWORK;

  const network = _.get(item, 'network');
  return network || NO_AUTHORIZING_NETWORK;
};

export const plurarize = ({ count, singular, plural, separator = ' ', reverse = false }) => {
  const label = count === 1 ? singular : plural;
  const text = reverse ? `${label}${separator}${count}` : `${count}${separator}${label}`;
  return text;
};

export const slugify = text => {
  const slug = text || '';
  const a = 'àáäâèéëêìíïîòóöôùúüûñçßÿœæŕśńṕẃǵǹḿǘẍźḧ·/_,:;άαβγδεέζήηθιίϊΐκλμνξοόπρσςτυϋύΰφχψωώ';
  const b = 'aaaaeeeeiiiioooouuuuncsyoarsnpwgnmuxzh------aavgdeeziitiiiiklmnxooprsstyyyyfhpoo';
  const p = new RegExp(a.split('').join('|'), 'g');

  return slug
    .toString()
    .trim()
    .toLowerCase()
    .replace(/ου/g, 'ou')
    .replace(/ευ/g, 'eu')
    .replace(/θ/g, 'th')
    .replace(/ψ/g, 'ps')
    .replace(/\//g, '-')
    .replace(Regex.SPACES, '-') // Replace spaces with -
    .replace(p, c => b.charAt(a.indexOf(c))) // Replace special chars
    .replace(/&/g, '-and-') // Replace & with 'and'
    .replace(Regex.NON_WORD_CHARACTERS, '') // Remove all non-word chars
    .replace(Regex.MULTIPLE_DASHES, '-') // Replace multiple - with single -
    .replace(Regex.LEADING_DASH, '') // Trim - from start of text
    .replace(Regex.TRAILING_DASH, ''); // Trim - from end of text
};

export const spaceToDash = text => text.split(' ').join('-');

export const formatUpcomingDate = dateString => {
  const startingDate = stringToDate(dateString);
  if (!startingDate) return null;
  const { epg_todayHeader: labelToday, epg_tomorrowHeader: labelTomorrow } = window.Language;
  const today = now();
  const tomorrow = addDays(today, 1);
  const videoStartsToday = isSameDay(startingDate, today);
  const videoStartsTomorrow = isSameDay(startingDate, tomorrow);
  const videoStartsNextWeek = differenceInDays(startingDate, today) >= 7;

  let dateLabel;
  if (videoStartsToday) {
    dateLabel = labelToday;
  } else if (videoStartsTomorrow) {
    dateLabel = labelTomorrow;
  } else if (videoStartsNextWeek) {
    dateLabel = format(startingDate, FORMAT_MONTH_SLASH_DAY);
  } else {
    dateLabel = format(startingDate, FORMAT_DAY_OF_WEEK);
  }
  const formattedTime = format(startingDate, FORMAT_TIME);

  return `${dateLabel}, ${formattedTime}`;
};

export const isUpcomingContent = item => {
  if (!item) return false;
  const { '@type': type, startDate } = item;
  const isLiveItem = isLive(type);
  const isInFuture = isAfter(startDate, now());
  const isUpcoming = isLiveItem && isInFuture;
  return isUpcoming;
};

export const isLiveVideoExpired = endDate => isAfter(now(), endDate);

export const isCurrentLiveContent = item => {
  if (!item) return false;
  const { '@type': type, endDate } = item;
  const itemIsLive = isLive(type);
  const itemIsExpired = isLiveVideoExpired(endDate);
  const itemIsUpcoming = isUpcomingContent(item);
  if (itemIsLive && !itemIsExpired && !itemIsUpcoming) return true;
  return false;
};

const getSecUntilTimestamp = time => differenceInSeconds(msToDate(time), now());

export const calculateNextRefreshForPanel = panel => {
  if (isStaticRender()) return false;
  const items = _.get(panel, 'items.member', []);
  const liveContent = _.filter(items, item => isCurrentLiveContent(item));
  const upcomingContent = _.filter(items, item => isUpcomingContent(item));
  if (!liveContent.length && !upcomingContent.length) return false;

  const dateStringToTimestamp = str => getTimestamp(stringToDate(str));
  const endTimestamps = liveContent.map(item => dateStringToTimestamp(item.endDate));
  const startTimestamps = upcomingContent.map(item => dateStringToTimestamp(item.startDate));
  const STARTING_SOON_TRESHOLD_MS = 5 * 60 * 1000;

  const startingSoonTimestamps = upcomingContent
    .map(item => dateStringToTimestamp(item.startDate) - STARTING_SOON_TRESHOLD_MS)
    .filter(timestamp => isAfter(msToDate(timestamp), now()));

  const timestamps = [...endTimestamps, ...startTimestamps, ...startingSoonTimestamps];
  let nextTimestamp = Math.min(...timestamps);
  let secUntilNextTimestamp = getSecUntilTimestamp(nextTimestamp);
  if (secUntilNextTimestamp === 0) {
    _.remove(timestamps, t => t === nextTimestamp);
    nextTimestamp = Math.min(...timestamps);
    secUntilNextTimestamp = getSecUntilTimestamp(nextTimestamp);
  }
  return secUntilNextTimestamp + 1; // add 1 to prevent refetching data too soon
};

export const shouldBlockEndcardsForTrailer = (video, preauthorizedResources) => {
  if (!video) return null;
  const { clipType, videoType, isTrailer } = video;
  const videoIsTrailer = (isClip(videoType) && isClipTrailer(clipType)) || isTrailer;
  if (videoIsTrailer) return true;
  const isEligible = canDisplayUpsell(preauthorizedResources);
  return isEligible;
};

export const getStickBannerData = state => {
  const members = _.get(state, 'collection.panels.member', []);
  const promoPanels = members.filter(member => member && member.panelType === PANEL_TYPE_PROMOS);
  const stickyBannerPanel = promoPanels.find(panel => {
    const promoType = _.get(panel, 'items.member.0.promoType', null);
    return promoType === 'stickyBanner';
  });
  const stickyBannerData = _.get(stickyBannerPanel, 'items.member.0', null);
  return stickyBannerData;
};

export const getRemainingTime = (time, currentTime = now()) => {
  const total = Date.parse(stringToDate(time)) - Date.parse(currentTime);
  return {
    days: Math.floor(total / (1000 * 60 * 60 * 24)),
    hours: Math.floor((total / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((total / 1000 / 60) % 60),
    seconds: Math.floor((total / 1000) % 60),
    total,
  };
};

export const videoIsTrailer = (videoType, clipType) => isClip(videoType) && isClipTrailer(clipType);
